import { ArrowLeft } from "lucide-react";
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaUpload, FaSpinner, FaEye } from "react-icons/fa";
import API from "../../config/config";
import { toast } from "react-toastify";
import AadhaarPreview from "../../components/Driver/AadhaarPreview"; // Adjust path if needed
import { compareNames } from "../../components/Driver/compareNames"; // adjust path
import { isValidFullName, isValidAadhaarNumber } from "../../utils/Validator";
import { useEffect } from "react"; // Add if not already present

const VerifyAadhaar = () => {
  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [aadhaarName, setAadhaarName] = useState("");
  const [aadhaarFrontFile, setAadhaarFrontFile] = useState(null);
  const [aadhaarBackFile, setAadhaarBackFile] = useState(null);
  const [existingAadhaarFrontUrl, setExistingAadhaarFrontUrl] = useState(null);
  const [existingAadhaarBackUrl, setExistingAadhaarBackUrl] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [requestId, setRequestId] = useState(null);
  const [showFullImageFront, setShowFullImageFront] = useState(false);
  const [showFullImageBack, setShowFullImageBack] = useState(false);
  const token = localStorage.getItem("userPublicToken");
  const navigate = useNavigate();

  const isValidAadhaarNumber = (num) => /^\d{12}$/.test(num);
  const isValidName = (name) => /^[A-Za-z\s]+$/.test(name.trim());

  const aadhaarFrontPreview = useMemo(
    () => aadhaarFrontFile && URL.createObjectURL(aadhaarFrontFile),
    [aadhaarFrontFile]
  );
  const aadhaarBackPreview = useMemo(
    () => aadhaarBackFile && URL.createObjectURL(aadhaarBackFile),
    [aadhaarBackFile]
  );

  useEffect(() => {
    const fetchAadhaarInfo = async () => {
      try {
        const response = await fetch(API.ADMIN.USERDASHBOARD, {
          headers: {
            Authorization: "Bearer " + token,
          },
        });

        if (response.ok) {
          const result = await response.json();
          console.log(result);
          const aadhaarDetails = result?.data?.aadhaarDetails;

          if (aadhaarDetails) {
            setAadhaarNumber(aadhaarDetails.aadhaarNumber || "");
            setAadhaarName(aadhaarDetails.fullName || "");
          }

          if (aadhaarDetails?.aadhaarfront || aadhaarDetails?.aadhaarback) {
            setExistingAadhaarFrontUrl(aadhaarDetails?.aadhaarfront || null);
            setExistingAadhaarBackUrl(aadhaarDetails?.aadhaarback || null);
          }
        }
      } catch (error) {
        console.error("Failed to load Aadhaar details", error);
      }
    };

    fetchAadhaarInfo();
  }, [token]);

  const handleNextStep = () => {
    if (!aadhaarNumber || !aadhaarName) {
      alert("Please fill in all required fields.");
      return;
    }

    if (!isValidAadhaarNumber(aadhaarNumber)) {
      alert("Aadhaar number must be 12 digits.");
      return;
    }

    if (!isValidName(aadhaarName)) {
      alert("Full Name must contain only letters and spaces.");
      return;
    }

    // Assume this is where you access the DL name:
    const dlName = localStorage.getItem("dlName") || "";

    if (!compareNames(aadhaarName, dlName)) {
      alert(
        "The name on Aadhaar does not match the name on your Driving License. Please enter the correct name."
      );
      return;
    }

    setStep(2);
  };

  const uploadAadhaarFile = async (file, side) => {
    const headers = new Headers();
    headers.append("Authorization", "Bearer " + token);

    const formdata = new FormData();
    formdata.append("documentFor", "Driver");
    formdata.append("type", side);
    formdata.append("file", file, file.name);

    const response = await fetch(API.ADMIN.DOCUMENT_UPLOAD, {
      method: "POST",
      headers,
      body: formdata,
    });

    if (!response.ok) throw new Error(`${side} Aadhaar upload failed.`);
    const data = await response.json();
    return data.document?.documentUrl || null;
  };

  const handleSubmit = async () => {
    if (
      (!aadhaarFrontFile && !existingAadhaarFrontUrl) ||
      (!aadhaarBackFile && !existingAadhaarBackUrl)
    ) {
      alert("Please upload both Aadhaar front and back images.");
      return;
    }

    setLoading(true);
    try {
      const frontUrl = aadhaarFrontFile
        ? await uploadAadhaarFile(aadhaarFrontFile, "aadhaar_front")
        : existingAadhaarFrontUrl;
      const backUrl = aadhaarBackFile
        ? await uploadAadhaarFile(aadhaarBackFile, "aadhaar_back")
        : existingAadhaarBackUrl;

      const payload = {
        aadhaarNumber,
        nameToMatch: aadhaarName,
        consent: "Y",
        consentText:
          "I hear by declare my consent agreement for fetching my information via ZOOP API",
        taskId: "08b01aa8-9487-4e6d-a0f0-c796839d6b77",
        aadhaarfront: frontUrl,
        aadhaarback: backUrl,
      };

      const response = await fetch(API.ADMIN.AADHAAR_GENERATE_KYC, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log(result);

      //if (!response.ok) throw new Error(result.message || "Failed");

      if (
        result.success === true &&
        result.message ===
          "OTP generation failed, but status saved as InProgress"
      ) {
        // ✅ Specific message matched — treat as "in progress"
        toast.info("Aadhaar verification is in progress.");
        setTimeout(() => navigate("/user-dashboard"), 1500);
      } else if (result.success) {
        // ✅ Normal success case — OTP sent
        toast.success("OTP sent successfully");
        setRequestId(result.data.requestId);
        setStep(4); // Move to OTP input step
      } else {
        // ❌ Failure from backend
        toast.error(result.message || "OTP generation failed");
      }
    } catch (error) {
      console.error(error);
      alert("Aadhaar verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      alert("Please enter the OTP.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        requestId,
        otp,
        consent: "Y",
        consentText:
          "I hear by declare my consent agreement for fetching my information via ZOOP API",
        taskId: "08b01aa8-9487-4e6d-a0f0-c796839d6b77",
      };

      const response = await fetch(API.ADMIN.AADHAAR_VERIFY_KYC, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok)
        throw new Error(result.message || "OTP verification failed");

      toast.success("Aadhaar OTP verification successful!");
      // Reset form and navigate after successful verification
      setTimeout(() => {
        setStep(1);
        setAadhaarNumber("");
        setAadhaarName("");
        setAadhaarFrontFile(null);
        setAadhaarBackFile(null);
        setExistingAadhaarFrontUrl(null);
        setExistingAadhaarBackUrl(null);
        setOtp("");
        setRequestId(null);
        navigate("/user-dashboard");
      }, 1500);
    } catch (error) {
      console.error(error);
      toast.error("OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto my-10 p-6 bg-white rounded-lg shadow-md">
      {step === 1 && (
        <>
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate("/user-dashboard")}
              className="mr-3 text-gray-700 hover:text-indigo-600"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800">
              Enter Aadhaar Card Details
            </h2>
          </div>
          <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
            <div>
              <label className="block font-medium">Aadhaar Number</label>
              <input
                type="text"
                value={aadhaarNumber}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only numbers, up to 12 digits
                  if (/^\d{0,12}$/.test(value)) {
                    setAadhaarNumber(value);
                  }
                }}
                className="w-full border px-3 py-2 rounded border-gray-300"
                placeholder="XXXX-XXXX-XXXX"
              />
            </div>
            <div>
              <label className="block font-medium">Full Name</label>
              <input
                type="text"
                value={aadhaarName}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only letters (upper/lowercase) and spaces
                  if (/^[A-Za-z\s]*$/.test(value)) {
                    setAadhaarName(value);
                  }
                }}
                className="w-full border px-3 py-2 rounded border-gray-300"
                placeholder="Full Name on Aadhaar"
              />
            </div>
            <button
              className="w-full bg-indigo-600 text-white py-2 rounded"
              onClick={handleNextStep}
            >
              Next
            </button>
          </form>
        </>
      )}

      {step === 2 && (
        <>
          <div className="flex items-center mb-6">
            <button
              onClick={() => setStep(1)}
              className="mr-3 text-gray-700 hover:text-indigo-600"
              type="button"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800">
              Upload Aadhaar Card Images
            </h2>
          </div>
          <div className="space-y-6">
            <div className="border p-4 rounded shadow-sm">
              <label className="block text-sm font-semibold mb-1">
                Upload Front Side of Aadhaar Card
              </label>
              <p className="text-xs text-gray-600 mb-2">
                Upload a clear picture of the front side of your Aadhaar card.
              </p>

              {/* Only show file input if no existing image and no selected file */}
              {!existingAadhaarFrontUrl && !aadhaarFrontFile && (
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    setAadhaarFrontFile(e.target.files[0]);
                    setExistingAadhaarFrontUrl(null);
                  }}
                />
              )}

              {(aadhaarFrontPreview || existingAadhaarFrontUrl) && (
                <div className="mt-3">
                  <img
                    src={aadhaarFrontPreview || existingAadhaarFrontUrl}
                    alt="Aadhaar Front Preview"
                    className="w-full rounded border"
                  />
                  <div className="flex gap-4 mt-4 justify-center">
                    {existingAadhaarFrontUrl && !aadhaarFrontFile && (
                      <button
                        onClick={() => setShowFullImageFront(true)}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                      >
                        <FaEye />
                        View Full Image
                      </button>
                    )}
                    <label className="cursor-pointer flex gap-2 items-center text-sm text-blue-600 hover:text-blue-800">
                      <FaUpload />
                      Change
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          setAadhaarFrontFile(e.target.files[0]);
                          setExistingAadhaarFrontUrl(null);
                        }}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="border p-4 rounded shadow-sm">
              <label className="block text-sm font-semibold mb-1">
                Upload Back Side of Aadhaar Card
              </label>
              <p className="text-xs text-gray-600 mb-2">
                Upload a clear picture of the back side of your Aadhaar card.
              </p>

              {/* Only show file input if no existing image and no selected file */}
              {!existingAadhaarBackUrl && !aadhaarBackFile && (
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    setAadhaarBackFile(e.target.files[0]);
                    setExistingAadhaarBackUrl(null);
                  }}
                />
              )}

              {(aadhaarBackPreview || existingAadhaarBackUrl) && (
                <div className="mt-3">
                  <img
                    src={aadhaarBackPreview || existingAadhaarBackUrl}
                    alt="Aadhaar Back Preview"
                    className="w-full rounded border"
                  />
                  <div className="flex gap-4 mt-4 justify-center">
                    {existingAadhaarBackUrl && !aadhaarBackFile && (
                      <button
                        onClick={() => setShowFullImageBack(true)}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                      >
                        <FaEye />
                        View Full Image
                      </button>
                    )}
                    <label className="cursor-pointer flex gap-2 items-center text-sm text-blue-600 hover:text-blue-800">
                      <FaUpload />
                      Change
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          setAadhaarBackFile(e.target.files[0]);
                          setExistingAadhaarBackUrl(null);
                        }}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

            <button
              className={`w-full bg-indigo-600 text-white py-2 rounded ${
                (!aadhaarFrontFile && !existingAadhaarFrontUrl) ||
                (!aadhaarBackFile && !existingAadhaarBackUrl)
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              onClick={() => setStep(3)}
              disabled={
                (!aadhaarFrontFile && !existingAadhaarFrontUrl) ||
                (!aadhaarBackFile && !existingAadhaarBackUrl)
              }
            >
              Next: Preview
            </button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <div className="flex items-center mb-4">
            <button
              onClick={() => setStep(2)}
              type="button"
              className="mr-3 text-gray-700 hover:text-indigo-600"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800">Preview</h2>
          </div>
          <div className="space-y-4">
            <AadhaarPreview
              aadhaarNumber={aadhaarNumber}
              aadhaarName={aadhaarName}
              frontPreview={aadhaarFrontPreview || existingAadhaarFrontUrl}
              backPreview={aadhaarBackPreview || existingAadhaarBackUrl}
            />

            <button
              className={`w-full bg-green-600 text-white py-2 rounded ${
                loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <FaSpinner className="animate-spin mr-2" />
                  Submitting...
                </div>
              ) : (
                "Confirm & Submit"
              )}
            </button>
          </div>
        </>
      )}

      {step === 4 && (
        <>
          <div className="flex items-center mb-4">
            <button
              onClick={() => setStep(3)}
              type="button"
              className="mr-3 text-gray-700 hover:text-indigo-600"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800">Enter OTP</h2>
          </div>
          <div className="space-y-4">
            <label className="block font-medium">Enter OTP</label>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className="w-full border px-3 py-2 rounded border-gray-300"
              placeholder="Enter the OTP received"
            />
            <button
              className={`w-full bg-blue-600 text-white py-2 rounded ${
                loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
              onClick={handleVerifyOtp}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <FaSpinner className="animate-spin mr-2" />
                  Verifying OTP...
                </div>
              ) : (
                "Verify OTP"
              )}
            </button>
          </div>
        </>
      )}

      {/* Full Image Modals */}
      {showFullImageFront && existingAadhaarFrontUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative max-w-4xl max-h-full p-4">
            <img
              src={existingAadhaarFrontUrl}
              alt="Aadhaar Front Full View"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <button
              onClick={() => setShowFullImageFront(false)}
              className="absolute top-2 right-2 bg-white text-gray-800 rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-100"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {showFullImageBack && existingAadhaarBackUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative max-w-4xl max-h-full p-4">
            <img
              src={existingAadhaarBackUrl}
              alt="Aadhaar Back Full View"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <button
              onClick={() => setShowFullImageBack(false)}
              className="absolute top-2 right-2 bg-white text-gray-800 rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-100"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerifyAadhaar;
